/**
 * @description We use isObject from @aviatakz, because lodash's isObject returns incorrect and obsolete type 'object'
 */
import { isObject } from '@aviata/type-guards';
import { isString, isUndefined } from 'lodash-es';
import { getAll, type RemoteConfig } from 'firebase/remote-config';

import { firebaseConfig } from './config';
import type {
  MaintenanceToasterConfig,
  AppRemoteConfig,
  LocalizedConfig,
} from './types';

type AppRemoteConfigKey = keyof AppRemoteConfig;

/**
 * @description Method to check if a config has values for different locales
 *
 * Every value in the remote config should has localization, so we check if it is present
 *
 * However, there might be configs that do not require localization.
 * For them we don't need to use this type guard
 */
const isLocalizedConfig = (data: unknown): data is LocalizedConfig<unknown> => {
  return isObject(data) && !isUndefined(data.ru) && !isUndefined(data.kk);
};

const isMaintenanceToasterGuard = (
  data: unknown,
): data is MaintenanceToasterConfig => {
  if (isLocalizedConfig(data)) {
    return Object.values(data).every(localizedData => {
      return isObject(localizedData) && isString(localizedData.text);
    });
  }
  return false;
};

const isSupportedKey = (key: string): key is AppRemoteConfigKey => {
  return key in firebaseConfig.defaultAppRemoteConfig;
};

const mapConfigToGuard = (key: AppRemoteConfigKey) => {
  switch (key) {
    case 'tours_maintenance_toaster':
      return isMaintenanceToasterGuard;
  }
};

const isSupportedAndValidConfig = (
  key: string,
  data: unknown,
): key is AppRemoteConfigKey => {
  if (isSupportedKey(key)) {
    const validateConfig = mapConfigToGuard(key);
    return validateConfig(data);
  }

  return false;
};

/**
 * @description Firebase remote config returns string and JSON as a plain string,
 * so we should check if the value is plain string or JSON and parse it
 */
const tryToParseConfig = (param: string) => {
  try {
    return JSON.parse(param);
  } catch {
    return param;
  }
};

/**
 * @description Method to parse remote config and check if values in the correct format
 *
 * This method checks if we handle the remote config values in the app and they are in the correct format
 * to prevent an app break if a field was accidentally removed or changed
 */
const parseRemoteConfig = (remoteConfig: RemoteConfig) => {
  const remoteConfigParams = getAll(remoteConfig);

  const validRemoteConfig = Object.keys(remoteConfigParams).reduce(
    (acc, key) => {
      const configValue = tryToParseConfig(remoteConfigParams[key].asString());
      if (isSupportedAndValidConfig(key, configValue)) {
        acc[key] = configValue;
      }
      return acc;
    },
    {} as AppRemoteConfig,
  );

  return {
    ...firebaseConfig.defaultAppRemoteConfig,
    ...validRemoteConfig,
  };
};

export const firebaseLib = {
  parseRemoteConfig,
};
